/**
 * Every word the TAPE's reorder says — the stops of the ⋯ menu, and the sentence the live region
 * reads after a drop.
 *
 * The mechanic lives in `entities/plan-draft`: `moveCommands` decides what a move means and
 * `dropGates` decides whether it may happen. Both answer in FACTS — a position, a total, a refusal
 * code — because `model/` in an entity holds no sentences. This is where those facts become Polish,
 * and it is the sequence counterpart of `dayMove.ts`.
 *
 * ── Why the two are separate modules rather than one with a mode flag ────────────────────────
 * The grid moves a card to a NAMED weekday and the tape moves it to a NUMBERED position, and the
 * two sentences differ in more than a noun: "Przeniesiono Push A na środę" needs the accusative of
 * a weekday name, while "Przeniesiono Push A na pozycję 3 z 5" needs a count. Sharing a key would
 * force a string vague enough to cover both, which is the failure `dayMove`'s own header already
 * names for the refusal vocabulary.
 *
 * ── There is no swap here, and that is the mechanic's difference ─────────────────────────────
 * A weekday can hold one day, so a weekly move onto an occupied column SWAPS. A tape position is a
 * slot in a list: a training moved to position 3 takes it and everything from there shifts down.
 * Nothing is displaced, so nothing has to be named as displaced.
 *
 * No React, no DOM, no query hook, no i18next: `t` arrives as a parameter.
 */

import type { DropRefusal, MoveOutcome } from '~/entities/plan-draft';

import type { MoveTargetView } from './dayCardView';
import { refusalWords } from './dayMove';
import type { Translate } from './builderCopy';

const KEY = 'web:planBuilder.dnd';

/**
 * Every position of the cycle, as move stops for one training.
 *
 * `occupants` is what already stands at each position, which is what turns "Pozycja 3" into
 * "Pozycja 3 — Nogi A". A trainer who cannot see what is there is choosing a number blind.
 *
 * The training's OWN position stays in the list, disabled and focusable, carrying its reason —
 * removing it would leave a trainer counting entries to work out where they are (view ADR §12).
 */
export function positionTargets(
  t: Translate,
  options: {
    /** Every training's name, by position. Its length is the number of stops. */
    readonly occupants: readonly string[];
    readonly currentIndex: number;
  },
): MoveTargetView[] {
  return options.occupants.map((name, index) => {
    const position = index + 1;
    if (index === options.currentIndex) {
      return {
        coordinate: index,
        label: t(`${KEY}.positionCurrent`, { position }),
        disabledReason: t(`${KEY}.positionCurrentReason`),
        preview: t(`${KEY}.positionCurrent`, { position }),
      };
    }
    return {
      coordinate: index,
      label: t(`${KEY}.position`, { position, name }),
      disabledReason: null,
      preview: t(`${KEY}.positionPreview`, { position, name }),
    };
  });
}

/**
 * What the live region says once a tape reorder has been dispatched.
 *
 * Every branch this mechanic can produce is worded, `unchanged` included — silence after a
 * deliberate drop reads as a broken control, and a trainer who dropped a tile back where it started
 * needs to be told nothing happened rather than left to check.
 */
export function positionMoveAnnouncement(
  t: Translate,
  outcome: MoveOutcome,
  context: { readonly sessionName: string; readonly from: number; readonly total: number },
): string {
  if (outcome.kind === 'refused') return refusalWords(t, outcome.reason satisfies DropRefusal);
  if (outcome.kind === 'unchanged') {
    return t(`${KEY}.positionUnchanged`, {
      name: context.sessionName,
      position: context.from + 1,
      total: context.total,
    });
  }
  if (outcome.kind !== 'movedDayPosition') return '';
  return t(`${KEY}.positionMoved`, {
    name: context.sessionName,
    position: outcome.position,
    total: outcome.total,
  });
}
